export const Client = (function () {
  "use strict"

  // Client constructor
  function Client() {
    this.state = synchronized_ // start state
  }

  Client.prototype.setState = function (state) {
    this.state = state
  }

  // Call this method when the user changes the document.
  Client.prototype.applyClient = function (operation) {
    this.setState(this.state.applyClient(this, operation))
  }

  // Call this method with a new operation from the server
  Client.prototype.applyServer = function (operation) {
    this.setState(this.state.applyServer(this, operation))
  }

  Client.prototype.serverAck = function () {
    this.setState(this.state.serverAck(this))
  }

  Client.prototype.serverRetry = function () {
    this.setState(this.state.serverRetry(this))
  }

  // Override this method.
  Client.prototype.sendOperation = function (operation) {
    throw new Error("sendOperation must be defined in child class")
  }

  // Override this method.
  Client.prototype.applyOperation = function (operation) {
    throw new Error("applyOperation must be defined in child class")
  }

  // In the 'Synchronized' state, there is no pending operation that the client
  // has sent to the server.
  function Synchronized() {}
  Client.Synchronized = Synchronized

  Synchronized.prototype.applyClient = function (client, operation) {
    // When the user makes an edit, send the operation to the server and
    // switch to the 'AwaitingConfirm' state
    client.sendOperation(operation)
    return new AwaitingConfirm(operation)
  }

  Synchronized.prototype.applyServer = function (client, operation) {
    // When we receive a new operation from the server, the operation can be
    // simply applied to the current document
    client.applyOperation(operation)
    return this
  }

  Synchronized.prototype.serverAck = function (client) {
    throw new Error("There is no pending operation.")
  }

  Synchronized.prototype.serverRetry = function (client) {
    throw new Error("There is no pending operation.")
  }

  // Singleton
  var synchronized_ = new Synchronized()

  // In the 'AwaitingConfirm' state, there's one operation the client has sent
  // to the server and is still waiting for an acknowledgement.
  function AwaitingConfirm(outstanding) {
    // Save the pending operation
    this.outstanding = outstanding
  }
  Client.AwaitingConfirm = AwaitingConfirm

  AwaitingConfirm.prototype.applyClient = function (client, operation) {
    // When the user makes an edit, don't send the operation immediately,
    // instead switch to 'AwaitingWithBuffer' state
    return new AwaitingWithBuffer(this.outstanding, operation)
  }

  AwaitingConfirm.prototype.applyServer = function (client, operation) {
    // This is another client's operation. Visualization:
    //
    //                   /\
    // this.outstanding /  \ operation
    //                 /    \
    //                 \    /
    //  pair[1]         \  / pair[0] (new outstanding)
    //  (can be applied  \/
    //  to the client's
    //  current document)
    var pair = this.outstanding.transform(operation)
    client.applyOperation(pair[1])
    return new AwaitingConfirm(pair[0])
  }

  AwaitingConfirm.prototype.serverAck = function (client) {
    // The client's operation has been acknowledged
    // => switch to synchronized state
    return synchronized_
  }

  AwaitingConfirm.prototype.serverRetry = function (client) {
    client.sendOperation(this.outstanding)
    return this
  }

  // In the 'AwaitingWithBuffer' state, the client is waiting for an operation
  // to be acknowledged by the server while buffering the edits the user makes
  function AwaitingWithBuffer(outstanding, buffer) {
    // Save the pending operation and the user's edits since then
    this.outstanding = outstanding
    this.buffer = buffer
  }
  Client.AwaitingWithBuffer = AwaitingWithBuffer

  AwaitingWithBuffer.prototype.applyClient = function (client, operation) {
    // Compose the user's changes onto the buffer
    var newBuffer = this.buffer.compose(operation)
    return new AwaitingWithBuffer(this.outstanding, newBuffer)
  }

  AwaitingWithBuffer.prototype.applyServer = function (client, operation) {
    // Operation comes from another client
    //
    //                       /\
    //     this.outstanding /  \ operation
    //                     /    \
    //                    /\    /
    //       this.buffer /  \* / pair1[0] (new outstanding)
    //                  /    \/
    //                  \    /
    //          pair2[1] \  / pair2[0] (new buffer)
    // the transformed    \/
    // operation -- can
    // be applied to the
    // client's current
    // document
    //
    // * pair1[1]
    var pair1 = this.outstanding.transform(operation)
    var pair2 = this.buffer.transform(pair1[1])
    client.applyOperation(pair2[1])
    return new AwaitingWithBuffer(pair1[0], pair2[0])
  }

  AwaitingWithBuffer.prototype.serverRetry = function (client) {
    // Merge with our buffer and resend.
    var outstanding = this.outstanding.compose(this.buffer)
    client.sendOperation(outstanding)
    return new AwaitingConfirm(outstanding)
  }

  AwaitingWithBuffer.prototype.serverAck = function (client) {
    // The pending operation has been acknowledged
    // => send buffer
    client.sendOperation(this.buffer)
    return new AwaitingConfirm(this.buffer)
  }

  return Client
})()
